/**
 * street-tea-stall.js
 * Mô hình 3D Quán Trà Đá Vỉa Hè Việt Nam chuẩn phong cách Voxel / Low-poly
 * Tích hợp bàn ghế nhựa con, biển hiệu "Trà Đá Vỉa Hè", ấm tích ủ chần bông,
 * khay chén trà đá bọt thủy tinh và điếu cày tre truyền thống.
 */

import * as THREE from 'three';
import { createCanvas } from '../core/canvas-texture.js';
import { CANVAS_FONTS } from '../core/fonts.js';

export class StreetTeaStall {
  constructor() {
    this.root = new THREE.Group();
    this.root.name = 'StreetTeaStall';
    this.disposables = [];

    this.buildStall();
  }

  buildStall() {
    // 1. Bàn nhựa vỉa hè (Màu xanh dương đặc trưng)
    const tableMat = new THREE.MeshStandardMaterial({ color: 0x0284c7, roughness: 0.6 });
    this.disposables.push(tableMat);

    const tableTopGeo = new THREE.BoxGeometry(0.7, 0.04, 0.5);
    const tableTop = new THREE.Mesh(tableTopGeo, tableMat);
    tableTop.position.set(0, 0.4, 0);
    tableTop.castShadow = true;
    tableTop.receiveShadow = true;
    this.root.add(tableTop);
    this.disposables.push(tableTopGeo);

    // 4 chân bàn nhựa
    const legGeo = new THREE.BoxGeometry(0.04, 0.4, 0.04);
    this.disposables.push(legGeo);
    [
      [-0.31, -0.21],
      [0.31, -0.21],
      [-0.31, 0.21],
      [0.31, 0.21]
    ].forEach(([lx, lz]) => {
      const leg = new THREE.Mesh(legGeo, tableMat);
      leg.position.set(lx, 0.2, lz);
      leg.castShadow = true;
      this.root.add(leg);
    });

    // 2. Ghế nhựa cóc mini vỉa hè (1 đỏ, 2 xanh)
    const redMat = new THREE.MeshStandardMaterial({ color: 0xdc2626, roughness: 0.6 });
    const blueMat = new THREE.MeshStandardMaterial({ color: 0x2563eb, roughness: 0.6 });
    this.disposables.push(redMat, blueMat);

    const stoolPositions = [
      { x: -0.55, z: 0, rot: 0.1, mat: redMat },
      { x: 0.55, z: 0.05, rot: -0.2, mat: blueMat },
      { x: 0, z: -0.45, rot: 0, mat: redMat }
    ];

    const stoolTopGeo = new THREE.BoxGeometry(0.28, 0.03, 0.28);
    const stoolLegGeo = new THREE.BoxGeometry(0.03, 0.22, 0.03);
    this.disposables.push(stoolTopGeo, stoolLegGeo);

    stoolPositions.forEach((st) => {
      const stool = new THREE.Group();
      stool.position.set(st.x, 0, st.z);
      stool.rotation.y = st.rot;

      const top = new THREE.Mesh(stoolTopGeo, st.mat);
      top.position.y = 0.22;
      top.castShadow = true;
      stool.add(top);

      [
        [-0.11, -0.11],
        [0.11, -0.11],
        [-0.11, 0.11],
        [0.11, 0.11]
      ].forEach(([sx, sz]) => {
        const sLeg = new THREE.Mesh(stoolLegGeo, st.mat);
        sLeg.position.set(sx, 0.11, sz);
        sLeg.castShadow = true;
        stool.add(sLeg);
      });

      this.root.add(stool);
    });

    // 3. Giỏ ấm tích ủ trà xanh bọc vải hoa
    const basketMat = new THREE.MeshStandardMaterial({ color: 0xb45309, roughness: 0.8 });
    const lidMat = new THREE.MeshStandardMaterial({ color: 0x78350f, roughness: 0.6 });
    const basketGeo = new THREE.CylinderGeometry(0.09, 0.08, 0.14, 12);
    const lidGeo = new THREE.CylinderGeometry(0.07, 0.09, 0.04, 12);
    this.disposables.push(basketMat, lidMat, basketGeo, lidGeo);

    const teapot = new THREE.Group();
    teapot.position.set(-0.2, 0.42 + 0.07, -0.1);

    const basketMesh = new THREE.Mesh(basketGeo, basketMat);
    teapot.add(basketMesh);

    const lidMesh = new THREE.Mesh(lidGeo, lidMat);
    lidMesh.position.y = 0.08;
    teapot.add(lidMesh);

    this.root.add(teapot);

    // 4. Khay nhôm đựng cốc trà đá
    const trayMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.6, roughness: 0.3 });
    const trayGeo = new THREE.BoxGeometry(0.24, 0.015, 0.18);
    this.disposables.push(trayMat, trayGeo);

    const tray = new THREE.Mesh(trayGeo, trayMat);
    tray.position.set(0.14, 0.42 + 0.008, 0.05);
    this.root.add(tray);

    // 2 Cốc trà đá thủy tinh vỉa hè
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0x67e8f9,
      transparent: true,
      opacity: 0.5,
      roughness: 0.1
    });
    const teaMat = new THREE.MeshStandardMaterial({ color: 0xd97706, roughness: 0.4 });
    const lemonMat = new THREE.MeshStandardMaterial({ color: 0xfacc15, roughness: 0.5 });
    const glassGeo = new THREE.CylinderGeometry(0.035, 0.028, 0.08, 10);
    const teaGeo = new THREE.CylinderGeometry(0.032, 0.026, 0.065, 10);
    const lemonGeo = new THREE.CylinderGeometry(0.02, 0.02, 0.005, 8);
    this.disposables.push(glassMat, teaMat, lemonMat, glassGeo, teaGeo, lemonGeo);

    [
      { x: 0.09, z: 0.05 },
      { x: 0.18, z: 0.07 }
    ].forEach((cPos) => {
      const cup = new THREE.Group();
      cup.position.set(cPos.x, 0.435 + 0.04, cPos.z);

      const glassMesh = new THREE.Mesh(glassGeo, glassMat);
      cup.add(glassMesh);

      const teaMesh = new THREE.Mesh(teaGeo, teaMat);
      teaMesh.position.y = -0.006;
      cup.add(teaMesh);

      const lemonMesh = new THREE.Mesh(lemonGeo, lemonMat);
      lemonMesh.rotation.x = Math.PI / 2;
      lemonMesh.position.set(0.02, 0.035, 0);
      cup.add(lemonMesh);

      this.root.add(cup);
    });

    // 5. Điếu cày tre truyền thống dựng tựa chân bàn
    const bambooMat = new THREE.MeshStandardMaterial({ color: 0x854d0e, roughness: 0.5 });
    const pipeGeo = new THREE.CylinderGeometry(0.022, 0.022, 0.55, 12);
    const bowlGeo = new THREE.CylinderGeometry(0.015, 0.012, 0.08, 8);
    this.disposables.push(bambooMat, pipeGeo, bowlGeo);

    const pipeGroup = new THREE.Group();
    pipeGroup.position.set(0.35, 0.25, -0.22);
    pipeGroup.rotation.z = -0.25;
    pipeGroup.rotation.x = 0.15;

    const pipeMesh = new THREE.Mesh(pipeGeo, bambooMat);
    pipeMesh.castShadow = true;
    pipeGroup.add(pipeMesh);

    const bowlMesh = new THREE.Mesh(bowlGeo, bambooMat);
    bowlMesh.position.set(0.02, 0.1, 0);
    bowlMesh.rotation.z = Math.PI / 3;
    pipeGroup.add(bowlMesh);

    this.root.add(pipeGroup);

    // 6. Biển hiệu "TRÀ ĐÁ VỈA HÈ" phong cách đường phố Việt Nam
    const signBoard = this.createSignBoard();
    signBoard.position.set(-0.75, 0, 0.35);
    signBoard.rotation.y = Math.PI * 0.15;
    this.root.add(signBoard);
  }

  createSignBoard() {
    const group = new THREE.Group();

    // Canvas vẽ biển hiệu
    const canvas = createCanvas(256, 320);
    const ctx = canvas.getContext('2d');

    // Nền vàng nắng vỉa hè
    ctx.fillStyle = '#fef08a';
    ctx.fillRect(0, 0, 256, 320);

    // Viền khung đỏ đậm
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 10;
    ctx.strokeRect(10, 10, 236, 300);

    // Dòng chữ chính "TRÀ ĐÁ"
    ctx.fillStyle = '#b91c1c';
    ctx.font = `bold 44px ${CANVAS_FONTS.sans}`;
    ctx.textAlign = 'center';
    ctx.fillText('TRÀ ĐÁ', 128, 75);

    // Dòng chữ phụ "VỈA HÈ"
    ctx.fillStyle = '#0369a1';
    ctx.font = `bold 36px ${CANVAS_FONTS.sans}`;
    ctx.fillText('VỈA HÈ', 128, 125);

    // Đường kẻ phân cách
    ctx.strokeStyle = '#ea580c';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(30, 145);
    ctx.lineTo(226, 145);
    ctx.stroke();

    // Các món nước
    ctx.fillStyle = '#1c1917';
    ctx.font = `bold 20px ${CANVAS_FONTS.sans}`;
    ctx.fillText('Nhân Trần • Mộc Bát', 128, 185);
    ctx.fillText('Cà Phê Phin • Thuốc Lào', 128, 225);

    // Giá bình dân
    ctx.fillStyle = '#047857';
    ctx.font = `bold 24px ${CANVAS_FONTS.sans}`;
    ctx.fillText('3.000đ / Cốc', 128, 275);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.disposables.push(texture);

    const signMat = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.7 });
    const signGeo = new THREE.BoxGeometry(0.48, 0.6, 0.02);
    this.disposables.push(signMat, signGeo);

    const signMesh = new THREE.Mesh(signGeo, signMat);
    signMesh.position.y = 0.52;
    signMesh.castShadow = true;
    group.add(signMesh);

    // Hai chân giá đỡ gỗ chữ A
    const woodMat = new THREE.MeshStandardMaterial({ color: 0x57534e, roughness: 0.8 });
    const postGeo = new THREE.CylinderGeometry(0.018, 0.018, 0.85, 8);
    this.disposables.push(woodMat, postGeo);

    [-0.2, 0.2].forEach((px) => {
      const post = new THREE.Mesh(postGeo, woodMat);
      post.position.set(px, 0.42, 0);
      post.castShadow = true;
      group.add(post);
    });

    return group;
  }

  dispose() {
    this.disposables.forEach((item) => {
      if (item && typeof item.dispose === 'function') {
        item.dispose();
      }
    });
  }
}
